<template>
  <div>
    <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div>
        <h2 class="text-1xl font-bold text-gray-700">{{ pageTitle }}</h2>
        <p class="mt-2 max-w-3xl text-sm text-slate-600">
          {{ pageDescription }}
        </p>
      </div>
      <div class="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
        Sucursal actual: <strong class="text-slate-800">{{ form.idsucursal || "-" }}</strong>
      </div>
    </div>

    <section v-if="isReceptionView" class="mt-5">
      <article class="overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm">
        <div class="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 px-5 py-4">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">Sección 1</p>
          <h3 class="mt-1 text-lg font-semibold text-slate-800">Datos del cliente</h3>
          <p class="text-sm text-slate-500">Identifica o crea el cliente antes de registrar la entrada del equipo.</p>
        </div>
        <div class="p-5">
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 class="text-lg font-semibold text-slate-800">Buscar cliente</h3>
              <p class="text-sm text-slate-500">
                Busca por nombre, documento, teléfono o WhatsApp antes de registrar la recepción.
              </p>
            </div>
            <div v-if="form.idcliente"
              class="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Cliente existente
            </div>
            <div v-else
              class="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
              Nuevo cliente
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-3 md:flex-row">
            <input v-model="customerSearch" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Nombre, documento, teléfono o WhatsApp" @keydown.enter.prevent="searchCustomers" />
            <div class="flex gap-2">
              <button
                class="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-300"
                :disabled="loadingCustomers" @click="searchCustomers">
                {{ loadingCustomers ? "Buscando..." : "Buscar cliente" }}
              </button>
              <button
                class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                @click="resetCustomerSelection">
                Limpiar
              </button>
            </div>
          </div>

          <div v-if="customerMatches.length" class="mt-4 space-y-2">
            <button v-for="customer in customerMatches" :key="customer.id"
              class="flex w-full items-start justify-between rounded-lg border border-slate-200 px-4 py-3 text-left hover:border-blue-300 hover:bg-blue-50"
              @click="selectCustomer(customer)">
              <div>
                <p class="text-sm font-semibold uppercase text-slate-800">{{ customer.nombres || "Cliente sin nombre" }}
                </p>
                <p class="mt-1 text-xs text-slate-500">
                  Doc: {{ customer.numero_documento || "S/D" }} | Tel: {{ customer.telefono || "S/D" }}
                </p>
              </div>
              <span class="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Usar</span>
            </button>
          </div>

          <p v-else-if="customerSearchAttempted" class="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
            No se encontraron coincidencias. Completa los datos debajo y el cliente se creará automáticamente al
            guardar.
          </p>

          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">Nombre del cliente</span>
              <input v-model="form.customer.names" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Nombre completo" />
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">Tipo de documento</span>
              <select v-model="form.customer.idTypeOfDocument"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500">
                <option :value="null">Selecciona un tipo</option>
                <option v-for="document in documentTypes" :key="document.id" :value="document.id">
                  {{ document.descripcion }}
                </option>
              </select>
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">Número de documento</span>
              <input v-model="form.customer.numberDocument" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Documento o identificación" />
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">Teléfono</span>
              <input v-model="form.customer.phone" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Teléfono principal" />
            </label>
            <label class="block md:col-span-2">
              <span class="mb-2 block text-sm font-medium text-slate-700">Dirección</span>
              <input v-model="form.customer.address" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Dirección del cliente" />
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">Correo</span>
              <input v-model="form.customer.email" type="email"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Correo electrónico" />
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-medium text-slate-700">WhatsApp</span>
              <input v-model="form.customer.whatsapp" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Número de WhatsApp" />
            </label>
          </div>

          <label class="mt-4 flex items-center gap-3 text-sm text-slate-700">
            <input v-model="form.customer.acepta_whatsapp" type="checkbox" class="h-4 w-4 rounded border-slate-300" />
            El cliente acepta comunicación por WhatsApp
          </label>
        </div>
      </article>

    </section>

    <section v-if="isReceptionView"
      class="mt-5 overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
      <div class="border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4">
        <p class="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">Sección 2</p>
        <div class="mt-1 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 class="text-lg font-semibold text-slate-800">Servicios y productos del trabajo</h3>
            <p class="text-sm text-slate-500">Agrega múltiples conceptos para un mismo equipo. El cobro se hace en
              caja, no en recepción.</p>
          </div>
          <div
            class="rounded-lg bg-emerald-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            Detalle multilinea
          </div>
        </div>
      </div>

      <div class="p-5">
        <div class="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <span class="block text-xl font-bold text-slate-800 md:text-2xl">Agregar producto / servicio</span>
              <p class="mt-1 text-xs text-slate-500">
                Busca productos o servicios (incluyendo chequeo y obsequios) y agrégalos al detalle de la orden.
              </p>
            </div>
            <div v-if="form.order_details.length"
              class="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
              {{ form.order_details.length }} línea(s)
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-3 md:flex-row">
            <input v-model="articleSearch" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Ej. Cambio de pantalla Samsung Galaxy S7" @keydown.enter.prevent="searchCatalogItems" />
            <div class="flex gap-2">
              <button
                class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                :disabled="loadingArticles" @click="searchCatalogItems">
                {{ loadingArticles ? "Buscando..." : "Buscar artículo" }}
              </button>
              <button
                class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                @click="clearCatalogItem">
                Limpiar búsqueda
              </button>
            </div>
          </div>

          <div v-if="articleMatches.length" class="mt-4 space-y-2">
            <button v-for="item in articleMatches" :key="item.id"
              class="flex w-full items-start justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left hover:border-blue-300 hover:bg-blue-50"
              @click="addCatalogItemAsDetail(item)">
              <div>
                <p class="text-sm font-semibold text-slate-800">{{ item.nombre_producto || item.descripcion }}</p>
                <p class="mt-1 text-xs text-slate-500">
                  Tipo: {{ item.catalogType === "service" ? "Servicio" : "Producto" }}
                  | Código: {{ item.codigo_de_barra || "S/C" }}
                  | Precio: {{ formatMoney(item.precio_de_venta) }}
                </p>
              </div>
              <span class="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Agregar</span>
            </button>
          </div>

          <p v-else-if="articleSearchAttempted" class="mt-4 rounded-lg bg-white px-4 py-3 text-sm text-slate-600">
            No se encontraron artículos coincidentes en el catálogo para esa búsqueda.
          </p>

          <div v-if="form.order_details.length"
            class="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table class="w-full text-sm">
              <thead class="bg-slate-100 text-slate-600">
                <tr>
                  <th class="px-3 py-2 text-left">Concepto</th>
                  <th class="px-3 py-2 text-left">Cant.</th>
                  <th class="px-3 py-2 text-left">Precio</th>
                  <th class="px-3 py-2 text-left">Subtotal</th>
                  <th class="px-3 py-2 text-left">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, index) in form.order_details" :key="detail.key" class="border-t border-slate-100">
                  <td class="px-3 py-2">
                    <input v-model="detail.descripcion" type="text"
                      class="w-full rounded-lg border border-slate-300 px-2 py-1 outline-none focus:border-blue-500"
                      placeholder="Descripción" />
                    <p v-if="detail.es_obsequio"
                      class="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Obsequio</p>
                  </td>
                  <td class="px-3 py-2">
                    <input v-model.number="detail.cantidad" type="number" min="0.01" step="0.01"
                      class="w-24 rounded-lg border border-slate-300 px-2 py-1 outline-none focus:border-blue-500" />
                  </td>
                  <td class="px-3 py-2">
                    <input v-model.number="detail.precio_unitario" type="number" min="0" step="0.01"
                      class="w-28 rounded-lg border border-slate-300 px-2 py-1 outline-none focus:border-blue-500" />
                  </td>
                  <td class="px-3 py-2 font-semibold text-slate-700">{{ formatMoney(detailSubtotal(detail)) }}</td>
                  <td class="px-3 py-2">
                    <button type="button"
                      class="rounded-md border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                      @click="removeOrderDetail(index)">
                      Quitar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else
            class="mt-4 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">
            Aún no has agregado líneas. Busca un servicio o producto y presiona "Agregar".
          </div>

          <div class="mt-4 flex items-center justify-end">
            <div class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
              Total estimado: {{ formatMoney(orderDetailsTotal) }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="isReceptionView" class="mt-5 overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm">
      <div class="border-b border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4">
        <p class="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">Sección 3</p>
        <div class="mt-1 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 class="text-lg font-semibold text-slate-800">Registro de recepción</h3>
            <p class="text-sm text-slate-500">Completa el detalle técnico del equipo y verifica la información antes de
              registrar la entrada.</p>
          </div>
          <div
            class="rounded-lg bg-amber-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
            Estado inicial: Recibido
          </div>
        </div>
      </div>

      <div class="p-5">
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">Tipo de equipo</span>
            <input v-model="form.tipo_equipo" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Celular, laptop, tablet..." />
          </label>
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">Marca</span>
            <input v-model="form.marca" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Marca" />
          </label>
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">Modelo</span>
            <input v-model="form.modelo" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Modelo" />
          </label>
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">Color</span>
            <input v-model="form.color" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Color" />
          </label>
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">IMEI</span>
            <input v-model="form.imei" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="IMEI" />
          </label>
          <label class="block">
            <span class="mb-2 block text-sm font-medium text-slate-700">Serial</span>
            <input v-model="form.serial" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Serial" />
          </label>
          <label class="block xl:col-span-3">
            <span class="mb-2 block text-sm font-medium text-slate-700">Accesorios</span>
            <input v-model="form.accesorios" type="text"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Cargador, funda, chip, batería, etc." />
          </label>
          <label class="block xl:col-span-3">
            <span class="mb-2 block text-sm font-medium text-slate-700">Problema reportado</span>
            <textarea v-model="form.problema_reportado" rows="4"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Describe el problema reportado por el cliente"></textarea>
          </label>
          <label class="block xl:col-span-3">
            <span class="mb-2 block text-sm font-medium text-slate-700">Observaciones</span>
            <textarea v-model="form.observaciones" rows="3"
              class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Detalles físicos, golpes, estado estético o condiciones adicionales"></textarea>
          </label>
          <div class="block xl:col-span-3">
            <span class="mb-2 block text-sm font-medium text-slate-700">Fotos del equipo</span>
            <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p class="text-sm font-semibold text-slate-800">Evidencia fotográfica</p>
                  <p class="mt-1 text-xs text-slate-500">Sube archivos existentes o abre la cámara del dispositivo para
                    capturar el equipo al momento.</p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button type="button"
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    @click="openPhotoPicker('gallery')">
                    Cargar foto
                  </button>
                  <button type="button"
                    class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    @click="openPhotoPicker('camera')">
                    Abrir cámara
                  </button>
                </div>
              </div>
              <input ref="galleryPhotoInput" type="file" class="hidden" accept="image/png,image/jpeg,image/webp"
                multiple @change="handlePhotoSelection" />
              <input ref="cameraPhotoInput" type="file" class="hidden" accept="image/png,image/jpeg,image/webp"
                capture="environment" multiple @change="handlePhotoSelection" />
            </div>

            <div v-if="selectedPhotos.length" class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <article v-for="(photo, index) in selectedPhotos" :key="photo.key"
                class="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <img :src="photo.preview" :alt="photo.name" class="h-40 w-full object-cover" />
                <div class="flex items-center justify-between gap-3 px-3 py-2">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-slate-700">{{ photo.name }}</p>
                    <p class="text-xs text-slate-500">{{ formatBytes(photo.size) }}</p>
                  </div>
                  <button
                    class="rounded-md border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                    @click="removeSelectedPhoto(index)">
                    Quitar
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>

        <div class="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p class="text-sm text-slate-500">
            Al guardar se genera automáticamente la orden y el enlace público seguro de seguimiento.
          </p>
          <button
            class="rounded-lg bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            :disabled="submitting" @click="submitOrder">
            {{ submitting ? "Registrando..." : "Registrar recepción" }}
          </button>
        </div>
      </div>
    </section>

    <section v-if="isReceptionView && submissionResult"
      class="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
      <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 class="text-lg font-semibold text-emerald-900">Recepción registrada</h3>
          <p class="mt-1 text-sm text-emerald-800">
            Orden {{ submissionResult.order?.codigo || "generada" }} para {{ submissionResult.order?.customer?.nombres
              || form.customer.names }}.
          </p>
          <p class="mt-2 text-sm text-emerald-700">
            {{ submissionCustomerMessage() }}
          </p>
        </div>
        <button
          class="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
          @click="copyPublicLink">
          Copiar enlace público
        </button>
        <div class="flex flex-wrap gap-2">
          <button
            class="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
            @click="printSubmissionReceipt('letter')">
            Imprimir carta
          </button>
          <button
            class="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
            @click="printSubmissionReceipt('ticket')">
            Imprimir 80 mm
          </button>
        </div>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-emerald-200 bg-white p-4">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">URL pública</p>
          <p class="mt-2 break-all text-sm text-slate-700">{{ submissionPublicTrackingUrl() }}</p>
        </div>
        <div class="rounded-lg border border-emerald-200 bg-white p-4">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Ruta interna</p>
          <p class="mt-2 break-all text-sm text-slate-700">{{ submissionResult.public_tracking_path }}</p>
        </div>
      </div>
    </section>

    <section v-if="isHistoryView" class="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="text-lg font-semibold text-slate-800">Equipos entregados</h3>
          <p class="text-sm text-slate-500">Listado de órdenes entregadas con filtro por cliente o correo.</p>
        </div>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
        <input v-model="historySearch.search" type="text"
          class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
          placeholder="Buscar por nombre del cliente o correo" />
        <button
          class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          :disabled="loadingHistory" @click="searchHistory">
          {{ loadingHistory ? "Buscando..." : "Buscar" }}
        </button>
      </div>

      <div v-if="historyResults.length" class="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-slate-100 text-slate-600">
              <tr>
                <th class="px-4 py-3 text-left font-semibold">Orden</th>
                <th class="px-4 py-3 text-left font-semibold">Equipo</th>
                <th class="px-4 py-3 text-left font-semibold">Cliente</th>
                <th class="px-4 py-3 text-left font-semibold">Correo</th>
                <th class="px-4 py-3 text-left font-semibold">Cobrado</th>
                <th class="px-4 py-3 text-left font-semibold">Estado</th>
                <th class="px-4 py-3 text-left font-semibold">Fecha entrega</th>
                <th class="px-4 py-3 text-left font-semibold">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in historyResults" :key="order.id" class="border-t border-slate-100">
                <td class="px-4 py-3 font-semibold text-slate-800">{{ order.codigo || "Sin código" }}</td>
                <td class="px-4 py-3 text-slate-700">{{ buildOrderDeviceLabel(order) }}</td>
                <td class="px-4 py-3 text-slate-700">{{ order.customer?.nombres || "Cliente no disponible" }}</td>
                <td class="px-4 py-3 text-slate-600">{{ order.customer?.correo || "Sin correo" }}</td>
                <td class="px-4 py-3 font-semibold text-slate-800">{{ formatMoney(order.monto_pagado_total || 0) }}</td>
                <td class="px-4 py-3">
                  <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em]"
                    :class="statusClass(order.estado)">
                    {{ formatStatus(order.estado) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-slate-600">{{ formatDate(order.fecha_entrega) }}</td>
                <td class="px-4 py-3">
                  <button
                    class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    @click="openHistoryDetail(order)">
                    Detalle
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p v-else-if="historyAttempted" class="mt-5 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
        No se encontraron equipos entregados con esos criterios.
      </p>

      <div v-if="historyDetailModalOpen && selectedOrder"
        class="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 px-4">
        <div class="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h4 class="text-base font-semibold text-slate-800">Detalle de orden {{
                historyOrderCodeLabel(selectedOrder) }}</h4>
              <p class="mt-1 text-xs text-slate-500">Información técnica, proceso y trabajo realizado.</p>
            </div>
            <button type="button"
              class="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              @click="closeHistoryDetail">
              Cerrar
            </button>
          </div>

          <div class="max-h-[calc(90vh-72px)] overflow-y-auto p-5">
            <div class="grid gap-4 md:grid-cols-2">
              <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                <p><strong>Equipo:</strong> {{ buildOrderDeviceLabel(selectedOrder) }}</p>
                <p class="mt-2"><strong>Cliente:</strong> {{ selectedOrder.customer?.nombres || "No disponible" }}</p>
                <p class="mt-2"><strong>Correo:</strong> {{ selectedOrder.customer?.correo || "Sin correo" }}</p>
                <p class="mt-2"><strong>Accesorios:</strong> {{ selectedOrder.accesorios || "S/D" }}</p>
                <p class="mt-2"><strong>IMEI:</strong> {{ selectedOrder.imei || "S/D" }}</p>
                <p class="mt-2"><strong>Serial:</strong> {{ selectedOrder.serial || "S/D" }}</p>
                <p class="mt-2"><strong>Recibido:</strong> {{ formatDate(selectedOrder.fecha_recepcion) }}</p>
                <p class="mt-2"><strong>Entregado:</strong> {{ formatDate(selectedOrder.fecha_entrega) }}</p>
                <p class="mt-2"><strong>Cobrado:</strong> {{ formatMoney(selectedOrder.monto_pagado_total || 0) }}</p>
                <p class="mt-2"><strong>Estado:</strong> {{ formatStatus(selectedOrder.estado) }}</p>
              </div>
              <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                <p><strong>Problema reportado:</strong></p>
                <p class="mt-2 whitespace-pre-line">{{ selectedOrder.problema_reportado || "Sin detalle" }}</p>
                <p class="mt-4"><strong>Diagnóstico:</strong></p>
                <p class="mt-2 whitespace-pre-line">{{ selectedOrder.diagnostico || "Sin diagnóstico" }}</p>
                <p class="mt-4"><strong>Solución aplicada:</strong></p>
                <p class="mt-2 whitespace-pre-line">{{ selectedOrder.solucion || "Sin solución registrada" }}</p>
                <p class="mt-4"><strong>Observaciones del proceso:</strong></p>
                <p class="mt-2 whitespace-pre-line">{{ selectedOrder.observaciones || "Sin observaciones" }}</p>
              </div>
            </div>

            <div class="mt-4 rounded-lg border border-slate-200 bg-white p-4">
              <p class="text-sm font-semibold text-slate-800">Detalle de trabajos y artículos</p>
              <div v-if="selectedOrder.details?.length" class="mt-3 overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-slate-100 text-slate-600">
                    <tr>
                      <th class="px-3 py-2 text-left">Descripción</th>
                      <th class="px-3 py-2 text-left">Cantidad</th>
                      <th class="px-3 py-2 text-left">Precio</th>
                      <th class="px-3 py-2 text-left">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="detail in selectedOrder.details" :key="detail.id || detail.descripcion"
                      class="border-t border-slate-100">
                      <td class="px-3 py-2 text-slate-700">{{ detail.descripcion || "Sin descripción" }}</td>
                      <td class="px-3 py-2 text-slate-700">{{ detail.cantidad || 0 }}</td>
                      <td class="px-3 py-2 text-slate-700">{{ formatMoney(detail.precio_unitario || 0) }}</td>
                      <td class="px-3 py-2 text-slate-800 font-semibold">{{ formatMoney((Number(detail.cantidad || 0) *
                        Number(detail.precio_unitario || 0))) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="mt-3 text-sm text-slate-500">Esta orden no tiene líneas de trabajo registradas.</p>
            </div>

            <div class="mt-4 flex flex-wrap justify-end gap-2">
              <button
                class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                @click="printSelectedOrderReceipt('letter')">
                Imprimir detalle carta
              </button>
              <button
                class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                @click="printSelectedOrderReceipt('ticket')">
                Imprimir detalle 80 mm
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="isListingView" class="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 class="text-lg font-semibold text-slate-800">Gestión de órdenes</h3>
          <p class="text-sm text-slate-500">
            Busca órdenes registradas, revisa el detalle y actualiza el estado operativo del taller.
          </p>
        </div>
        <div class="rounded-lg bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
          Control interno
        </div>
      </div>

      <div class="mt-5 grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
        <article class="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div class="grid gap-3 md:grid-cols-[1fr_220px_auto]">
            <label class="block">
              <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Buscar
                orden</span>
              <input v-model="orderFilters.search" type="text"
                class="w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                placeholder="Código, cliente, IMEI, serial, marca o artículo..." @keydown.enter.prevent="loadOrders" />
            </label>
            <label class="block">
              <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Filtro del
                listado</span>
              <select v-model="orderFilters.status"
                class="w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500">
                <option value="">Todos los estados</option>
                <option v-for="status in workshopFilterStatusOptions" :key="status" :value="status">
                  {{ formatStatus(status) }}
                </option>
              </select>
              <p class="mt-1 text-xs text-slate-500">Este control solo filtra la lista. No cambia el estado del equipo.
              </p>
            </label>
            <button type="button"
              class="mt-auto rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
              :disabled="loadingOrders" @click="loadOrders">
              {{ loadingOrders ? "Buscando..." : "Buscar" }}
            </button>
          </div>

          <div class="mt-4 space-y-3">
            <button v-for="order in orders" :key="order.id"
              class="w-full rounded-xl border px-4 py-4 text-left transition-colors"
              :class="selectedOrder?.id === order.id ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'"
              @click="selectOrder(order)">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-slate-800">{{ order.codigo || 'Sin código' }}</p>
                  <p class="mt-1 text-xs text-slate-500">
                    {{ order.customer?.nombres || 'Cliente no disponible' }}
                  </p>
                </div>
                <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em]"
                  :class="statusClass(order.estado)">
                  {{ formatStatus(order.estado) }}
                </span>
              </div>
              <p class="mt-3 text-sm text-slate-600">
                {{ buildOrderDeviceLabel(order) }}
              </p>
              <p v-if="order.articulo_referencia" class="mt-2 text-xs text-slate-500">
                Concepto: {{ order.articulo_referencia }}
              </p>
              <p class="mt-2 text-xs text-slate-500">
                IMEI: {{ order.imei || 'S/D' }} | Serial: {{ order.serial || 'S/D' }}
              </p>
            </button>

            <div v-if="!orders.length && ordersLoaded"
              class="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
              No se encontraron órdenes con esos filtros.
            </div>
          </div>

          <div v-if="ordersPagination.totalPages > 1" class="mt-4 flex items-center justify-between">
            <p class="text-xs text-slate-500">
              Página {{ ordersPagination.currentPage }} de {{ ordersPagination.totalPages }}
            </p>
            <div class="flex gap-2">
              <button
                class="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="ordersPagination.currentPage <= 1 || loadingOrders" @click="changeOrdersPage(-1)">
                Anterior
              </button>
              <button
                class="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="ordersPagination.currentPage >= ordersPagination.totalPages || loadingOrders"
                @click="changeOrdersPage(1)">
                Siguiente
              </button>
            </div>
          </div>
        </article>

        <article class="rounded-xl border border-slate-200 bg-white p-4">
          <div v-if="selectedOrder" class="space-y-5">
            <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h4 class="text-lg font-semibold text-slate-800">{{ selectedOrder.codigo || 'Orden seleccionada' }}</h4>
                <p class="mt-1 text-sm text-slate-500">
                  {{ selectedOrder.customer?.nombres || 'Cliente no disponible' }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em]"
                  :class="statusClass(selectedOrder.estado)">
                  {{ formatStatus(selectedOrder.estado) }}
                </span>
                <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em]"
                  :class="selectedOrder.habilitar_consulta_publica ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'">
                  {{ selectedOrder.habilitar_consulta_publica ? 'Consulta pública activa' : 'Consulta pública cerrada'
                  }}
                </span>
              </div>
            </div>

            <div class="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div class="flex-1">
                  <p class="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Cambio persistente</p>
                  <h5 class="mt-1 text-lg font-semibold text-slate-800">Actualizar estado del equipo</h5>
                  <p class="mt-1 text-sm text-slate-600">Este control sí guarda el estado del equipo en el sistema y
                    también actualiza la consulta pública del cliente.</p>
                </div>
                <div class="grid gap-3 md:grid-cols-[minmax(0,260px)_auto] lg:min-w-[420px]">
                  <label class="block">
                    <span class="mb-2 block text-sm font-medium text-slate-700">Nuevo estado</span>
                    <select v-model="statusForm.estado"
                      class="w-full rounded-lg border-2 border-blue-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500">
                      <option v-for="status in workshopUpdateStatusOptions" :key="status" :value="status">
                        {{ formatStatus(status) }}
                      </option>
                    </select>
                  </label>
                  <button type="button"
                    class="mt-auto rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                    :disabled="updatingStatus" @click="updateSelectedOrderStatus">
                    {{ updatingStatus ? 'Actualizando...' : 'Guardar estado' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                <p><strong>Equipo:</strong> {{ [selectedOrder.tipo_equipo, selectedOrder.marca,
                selectedOrder.modelo].filter(Boolean).join(' - ') || 'No especificado' }}</p>
                <p class="mt-2"><strong>Artículo/servicio:</strong> {{ selectedOrder.articulo_referencia ||
                  selectedOrder.product_reference?.nombre_producto || 'S/D' }}</p>
                <p class="mt-2"><strong>Accesorios:</strong> {{ selectedOrder.accesorios || 'S/D' }}</p>
                <p class="mt-2"><strong>IMEI:</strong> {{ selectedOrder.imei || 'S/D' }}</p>
                <p class="mt-2"><strong>Serial:</strong> {{ selectedOrder.serial || 'S/D' }}</p>
                <p class="mt-2"><strong>Recepción:</strong> {{ formatDate(selectedOrder.fecha_recepcion) }}</p>
                <p class="mt-2"><strong>Entrega:</strong> {{ formatDate(selectedOrder.fecha_entrega) }}</p>
              </div>
              <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                <p><strong>Problema reportado:</strong></p>
                <p class="mt-2 whitespace-pre-line">{{ selectedOrder.problema_reportado || 'Sin detalle' }}</p>
              </div>
            </div>

            <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p class="text-sm font-semibold text-slate-800">Reimpresión de recibo</p>
                  <p class="mt-1 text-xs text-slate-500">Genera nuevamente el recibo de recepción en formato carta o
                    ticket de 80 mm.</p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button v-if="hasPublicTrackingLink(selectedOrder) && selectedOrder.habilitar_consulta_publica"
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    @click="copySelectedOrderPublicLink">
                    Copiar ruta pública
                  </button>
                  <button
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    @click="printSelectedOrderReceipt('letter')">
                    Reimprimir carta
                  </button>
                  <button
                    class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    @click="printSelectedOrderReceipt('ticket')">
                    Reimprimir 80 mm
                  </button>
                </div>
              </div>
            </div>

            <div v-if="selectedOrder.photos?.length" class="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p class="text-sm font-semibold text-slate-800">Fotos registradas</p>
              <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <a v-for="photo in selectedOrder.photos" :key="photo.id" :href="photo.foto" target="_blank"
                  rel="noopener noreferrer"
                  class="overflow-hidden rounded-lg border border-slate-200 bg-white hover:border-blue-300">
                  <img :src="photo.foto" :alt="photo.nombre_archivo || 'Foto del equipo'"
                    class="h-36 w-full object-cover" />
                  <div class="px-3 py-2 text-xs text-slate-500">{{ photo.nombre_archivo || 'Foto del equipo' }}</div>
                </a>
              </div>
            </div>

            <div v-else
              class="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Esta orden no tiene fotos registradas.
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <label class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Diagnóstico</span>
                <textarea v-model="statusForm.diagnostico" rows="3"
                  class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  placeholder="Diagnóstico técnico"></textarea>
              </label>
              <label class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Solución</span>
                <textarea v-model="statusForm.solucion" rows="3"
                  class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  placeholder="Trabajo realizado o solución propuesta"></textarea>
              </label>
              <label class="block md:col-span-2">
                <span class="mb-2 block text-sm font-medium text-slate-700">Observaciones internas</span>
                <textarea v-model="statusForm.observaciones" rows="3"
                  class="w-full rounded-lg border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  placeholder="Observaciones del proceso"></textarea>
              </label>
            </div>

            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p class="text-sm text-slate-500">
                Si marcas la orden como entregada o cancelada, el enlace público queda cerrado automáticamente.
              </p>
              <button
                class="rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                :disabled="updatingStatus" @click="updateSelectedOrderStatus">
                {{ updatingStatus ? 'Actualizando...' : 'Actualizar estado' }}
              </button>
            </div>
          </div>

          <div v-else
            class="flex min-h-[280px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-sm text-slate-500">
            Selecciona una orden del panel izquierdo para revisar su detalle y actualizar el estado.
          </div>
        </article>
      </div>
    </section>
    <div v-if="cameraModalOpen" class="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 px-4">
      <div class="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h4 class="text-base font-semibold text-slate-800">Capturar foto del equipo</h4>
          <button type="button"
            class="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            @click="closeCameraModal">
            Cerrar
          </button>
        </div>

        <div class="bg-black">
          <video ref="cameraVideo" class="max-h-[65vh] w-full object-contain" autoplay playsinline muted></video>
        </div>

        <div class="flex flex-wrap items-center justify-end gap-2 px-5 py-4">
          <button type="button"
            class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            @click="switchCameraFacingMode">
            Cambiar cámara
          </button>
          <button type="button"
            class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-500"
            :disabled="cameraCaptureInProgress" @click="capturePhotoFromCamera">
            {{ cameraCaptureInProgress ? "Capturando..." : "Capturar" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { confirmationOfRecord, errorActions, successAction, warningActions } from "@/alerts";
import { findSubmoduleIdByDescription } from "@/hisAcces";
import { idLocalStore, idLocalUser } from "@/publicjs/localStorage";
import { printWorkshopReceipt } from "./printWorkshopReceipt";
import { useRoute } from "vue-router";

const buildInitialCustomer = () => ({
  idTypeOfDocument: null,
  names: "",
  numberDocument: "",
  phone: "",
  address: "",
  email: "",
  whatsapp: "",
  acepta_whatsapp: false,
});

const buildInitialForm = () => ({
  idsucursal: 0,
  idusuario_recepcion: 0,
  idcliente: null,
  customer: buildInitialCustomer(),
  tipo_equipo: "",
  marca: "",
  modelo: "",
  color: "",
  imei: "",
  serial: "",
  order_details: [],
  accesorios: "",
  problema_reportado: "",
  observaciones: "",
});

export default {
  name: "WorkshopView",
  setup() {
    const route = useRoute();
    return { route };
  },
  data() {
    return {
      documentTypes: [],
      customerSearch: "",
      customerMatches: [],
      customerSearchAttempted: false,
      loadingCustomers: false,
      articleSearch: "",
      articleMatches: [],
      articleSearchAttempted: false,
      loadingArticles: false,
      loadingHistory: false,
      historyAttempted: false,
      historySearch: {
        search: "",
      },
      historyResults: [],
      historyDetailModalOpen: false,
      loadingOrders: false,
      ordersLoaded: false,
      orders: [],
      selectedOrder: null,
      selectedPhotos: [],
      cameraModalOpen: false,
      cameraStream: null,
      cameraFacingMode: "environment",
      cameraCaptureInProgress: false,
      updatingStatus: false,
      orderFilters: {
        search: "",
        status: "",
        page: 1,
      },
      ordersPagination: {
        currentPage: 1,
        totalPages: 1,
      },
      statusForm: {
        estado: "RECIBIDO",
        diagnostico: "",
        solucion: "",
        observaciones: "",
        presupuesto: "",
        monto_pagado_total: "",
      },
      submitting: false,
      submissionResult: null,
      form: buildInitialForm(),
      statusOptions: [
        "RECIBIDO",
        "EN_DIAGNOSTICO",
        "EN_REPARACION",
        "LISTO_PARA_ENTREGA",
        "ENTREGADO",
        "CANCELADO",
      ],
    };
  },
  computed: {
    isReceptionView() {
      return this.route.name === "Workshop";
    },
    isHistoryView() {
      return this.route.name === "WorkshopHistory";
    },
    isListingView() {
      return this.route.name === "WorkshopList";
    },
    pageTitle() {
      if (this.isListingView) {
        return "Listado de equipos";
      }

      if (this.isHistoryView) {
        return "Historial de equipos";
      }

      return "Recepción de equipos";
    },
    pageDescription() {
      if (this.isListingView) {
        return "Gestiona todos los equipos ya recepcionados y actualiza el estado operativo del taller.";
      }

      if (this.isHistoryView) {
        return "Consulta equipos entregados y filtra por nombre de cliente o correo.";
      }

      return "Registra la entrada del equipo, vincula un artículo o servicio del catálogo y entrega el enlace público de seguimiento en el mismo flujo.";
    },
    orderDetailsTotal() {
      return this.form.order_details.reduce((accumulator, detail) => {
        return accumulator + this.detailSubtotal(detail);
      }, 0);
    },
    workshopFilterStatusOptions() {
      return this.statusOptions.filter((status) => status !== "ENTREGADO");
    },
    workshopUpdateStatusOptions() {
      return this.statusOptions.filter((status) => status !== "ENTREGADO");
    },
  },
  async mounted() {
    if (!findSubmoduleIdByDescription(this.route.path)) {
      window.location.href = "/login";
      return;
    }

    this.syncDocumentTitle();

    this.form.idsucursal = await idLocalStore();
    this.form.idusuario_recepcion = await idLocalUser();
    await this.loadDocumentTypes();

    if (this.isListingView) {
      await this.loadOrders();
    }

    if (this.isHistoryView) {
      await this.searchHistory();
    }
  },
  watch: {
    "route.fullPath"() {
      this.syncDocumentTitle();

      if (this.isListingView) {
        if (!this.ordersLoaded) {
          this.loadOrders();
        }
      }
    },
  },
  methods: {
    syncDocumentTitle() {
      document.title =
        this.route.meta.title + process.env.VUE_APP_TITLE ||
        process.env.VUE_APP_TITLE;
    },
    async loadDocumentTypes() {
      try {
        const response = await api.get("tipo_de_documentos/?page=1&per_page=200");
        this.documentTypes = response.data?.data || [];
        this.form.customer.idTypeOfDocument = this.documentTypes[0]?.id || null;
      } catch (error) {
        errorActions(error);
      }
    },
    syncOrderInListing(updatedOrder) {
      if (!updatedOrder?.id) {
        return;
      }

      const existingIndex = this.orders.findIndex((order) => order.id === updatedOrder.id);

      if (existingIndex === -1) {
        return;
      }

      const mergedOrder = {
        ...this.orders[existingIndex],
        ...updatedOrder,
      };

      this.orders.splice(existingIndex, 1, mergedOrder);

      if (this.selectedOrder?.id === updatedOrder.id) {
        this.selectedOrder = mergedOrder;
      }
    },
    async searchCustomers() {
      if (this.customerSearch.trim().length < 2) {
        warningActions("Ingresa al menos dos caracteres para buscar un cliente.");
        return;
      }

      this.loadingCustomers = true;
      this.customerSearchAttempted = true;
      this.customerMatches = [];

      try {
        const response = await api.get(
          `taller/clientes/buscar?search=${encodeURIComponent(this.customerSearch.trim())}`,
        );
        this.customerMatches = response.data?.data || [];

        if (!this.customerMatches.length) {
          this.form.idcliente = null;
        }
      } catch (error) {
        errorActions(error);
      } finally {
        this.loadingCustomers = false;
      }
    },
    async searchCatalogItems() {
      if (!this.form.idsucursal) {
        errorActions("No se pudo identificar la sucursal actual para buscar artículos.");
        return;
      }

      if (this.articleSearch.trim().length < 2) {
        warningActions("Ingresa al menos dos caracteres para buscar un artículo del catálogo.");
        return;
      }

      this.loadingArticles = true;
      this.articleSearchAttempted = true;
      this.articleMatches = [];

      try {
        const [productsResponse, servicesResponse] = await Promise.all([
          api.get(
            `productos-para-venta?idstore=${this.form.idsucursal}&search=${encodeURIComponent(this.articleSearch.trim())}`,
          ),
          api.get(`servicios?search=${encodeURIComponent(this.articleSearch.trim())}`),
        ]);

        const products = Array.isArray(productsResponse.data)
          ? productsResponse.data.map((item) => ({
            ...item,
            catalogType: "product",
          }))
          : [];

        const services = Array.isArray(servicesResponse.data)
          ? servicesResponse.data.map((item) => ({
            ...item,
            nombre_producto: item.descripcion,
            precio_de_venta:
              item.precio_de_venta !== undefined && item.precio_de_venta !== null
                ? Number(item.precio_de_venta)
                : (item.precio !== undefined && item.precio !== null ? Number(item.precio) : 0),
            codigo_de_barra: null,
            catalogType: "service",
          }))
          : [];

        this.articleMatches = [...products, ...services].slice(0, 10);
      } catch (error) {
        errorActions(error);
      } finally {
        this.loadingArticles = false;
      }
    },
    buildOrderDetailFromCatalogItem(item) {
      const description = item.nombre_producto || item.descripcion || "Servicio de taller";
      const unitPrice = item.precio_de_venta !== undefined && item.precio_de_venta !== null
        ? Number(item.precio_de_venta)
        : 0;

      return {
        key: `${item.catalogType || "item"}-${item.id || description}-${Date.now()}-${Math.random()}`,
        idproducto: item.id || null,
        descripcion: description,
        cantidad: 1,
        precio_unitario: Number.isFinite(unitPrice) ? unitPrice : 0,
        es_obsequio: Number(unitPrice || 0) === 0,
      };
    },
    async addCatalogItemAsDetail(item) {
      if (item?.catalogType === "product" && Number(item?.tipo || 0) === 1) {
        try {
          const response = await api.get(`productos-para-venta/${item.id}?stock=1`);

          if (response.data !== true) {
            warningActions(`El producto ${item.nombre_producto || item.descripcion || "seleccionado"} no tiene stock suficiente.`);
            return;
          }
        } catch (error) {
          errorActions(error);
          return;
        }
      }

      const detail = this.buildOrderDetailFromCatalogItem(item);
      this.form.order_details.push(detail);

      this.articleSearch = "";
      this.articleMatches = [];
      this.articleSearchAttempted = false;
    },
    removeOrderDetail(index) {
      this.form.order_details.splice(index, 1);
    },
    detailSubtotal(detail) {
      const quantity = Number(detail?.cantidad || 0);
      const unitPrice = Number(detail?.precio_unitario || 0);
      if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
        return 0;
      }

      return Math.max(quantity, 0) * Math.max(unitPrice, 0);
    },
    clearCatalogItem() {
      this.articleSearch = "";
      this.articleMatches = [];
      this.articleSearchAttempted = false;
    },
    selectCustomer(customer) {
      this.form.idcliente = customer.id;
      this.form.customer = {
        idTypeOfDocument: customer.idtipo_documento || this.documentTypes[0]?.id || null,
        names: customer.nombres || "",
        numberDocument: customer.numero_documento || "",
        phone: customer.telefono || "",
        address: customer.direccion || "",
        email: customer.correo || "",
        whatsapp: customer.whatsapp || customer.telefono || "",
        acepta_whatsapp: Boolean(customer.acepta_whatsapp),
      };
      this.customerSearch = customer.nombres || "";
      this.customerMatches = [];
      this.customerSearchAttempted = false;
    },
    resetCustomerSelection() {
      const documentTypeId = this.form.customer.idTypeOfDocument || this.documentTypes[0]?.id || null;
      this.form.idcliente = null;
      this.form.customer = {
        ...buildInitialCustomer(),
        idTypeOfDocument: documentTypeId,
      };
      this.customerSearch = "";
      this.customerMatches = [];
      this.customerSearchAttempted = false;
    },
    async searchHistory() {
      this.loadingHistory = true;
      this.historyAttempted = true;
      this.historyResults = [];

      try {
        const params = new URLSearchParams();
        if (this.historySearch.search.trim()) {
          params.set("search", this.historySearch.search.trim());
        }

        const response = await api.get(`taller/historial-equipo?${params.toString()}`);
        this.historyResults = response.data?.data || [];
      } catch (error) {
        errorActions(error);
      } finally {
        this.loadingHistory = false;
      }
    },
    openHistoryDetail(order) {
      if (!order?.id) {
        warningActions("No se pudo cargar el detalle de esta orden.");
        return;
      }

      this.selectedOrder = { ...order };
      this.historyDetailModalOpen = true;
    },
    closeHistoryDetail() {
      this.historyDetailModalOpen = false;
      this.selectedOrder = null;
    },
    async submitOrder() {
      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;

      try {
        const formData = new FormData();
        formData.append("idsucursal", this.form.idsucursal);
        formData.append("idusuario_recepcion", this.form.idusuario_recepcion);
        if (this.form.idcliente) {
          formData.append("idcliente", this.form.idcliente);
        }
        formData.append("tipo_equipo", this.form.tipo_equipo.trim());
        this.appendOptionalToFormData(formData, "marca", this.form.marca);
        this.appendOptionalToFormData(formData, "modelo", this.form.modelo);
        this.appendOptionalToFormData(formData, "color", this.form.color);
        this.appendOptionalToFormData(formData, "imei", this.form.imei);
        this.appendOptionalToFormData(formData, "serial", this.form.serial);
        this.form.order_details.forEach((detail, index) => {
          if (detail.idproducto) {
            formData.append(`details[${index}][idproducto]`, String(detail.idproducto));
          }
          formData.append(`details[${index}][descripcion]`, String(detail.descripcion || "").trim());
          formData.append(`details[${index}][cantidad]`, String(Number(detail.cantidad || 0)));
          formData.append(`details[${index}][precio_unitario]`, String(Number(detail.precio_unitario || 0)));
          formData.append(`details[${index}][es_obsequio]`, detail.es_obsequio ? "1" : "0");
        });
        this.appendOptionalToFormData(formData, "accesorios", this.form.accesorios);
        this.appendOptionalToFormData(formData, "observaciones", this.form.observaciones);
        formData.append("problema_reportado", this.form.problema_reportado.trim());
        formData.append("customer[idTypeOfDocument]", this.form.customer.idTypeOfDocument || "");
        formData.append("customer[names]", this.form.customer.names.trim());
        this.appendOptionalToFormData(formData, "customer[numberDocument]", this.form.customer.numberDocument);
        this.appendOptionalToFormData(formData, "customer[phone]", this.form.customer.phone);
        this.appendOptionalToFormData(formData, "customer[address]", this.form.customer.address);
        this.appendOptionalToFormData(formData, "customer[email]", this.form.customer.email);
        this.appendOptionalToFormData(formData, "customer[whatsapp]", this.form.customer.whatsapp);
        formData.append("customer[acepta_whatsapp]", this.form.customer.acepta_whatsapp ? 1 : 0);

        this.selectedPhotos.forEach((photo) => {
          formData.append("photos[]", photo.file);
        });

        const response = await api.post("taller/ordenes", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.submissionResult = response.data;
        confirmationOfRecord("La recepción del equipo", {
          title: "Orden registrada",
        });
        this.resetFormAfterSubmit(response.data?.order?.customer);
      } catch (error) {
        errorActions(error);
      } finally {
        this.submitting = false;
      }
    },
    validateForm() {
      if (!this.form.idsucursal || !this.form.idusuario_recepcion) {
        errorActions("No se pudo identificar la sucursal o el usuario actual.");
        return false;
      }

      if (this.form.customer.names.trim().length < 3) {
        errorActions("Ingresa el nombre del cliente.");
        return false;
      }

      if (this.form.tipo_equipo.trim().length < 2) {
        errorActions("Indica el tipo de equipo recibido.");
        return false;
      }

      if (!this.form.imei.trim() && !this.form.serial.trim()) {
        errorActions("Registra al menos un IMEI o un serial del equipo.");
        return false;
      }

      if (this.form.problema_reportado.trim().length < 5) {
        errorActions("Describe el problema reportado por el cliente.");
        return false;
      }

      if (!this.form.order_details.length) {
        errorActions("Agrega al menos un servicio o producto para esta recepción.");
        return false;
      }

      const invalidDetail = this.form.order_details.find((detail) => {
        return !String(detail.descripcion || "").trim() || Number(detail.cantidad || 0) <= 0 || Number(detail.precio_unitario || 0) < 0;
      });

      if (invalidDetail) {
        errorActions("Revisa el detalle de líneas: descripción, cantidad y precio deben ser válidos.");
        return false;
      }

      return true;
    },
    resetFormAfterSubmit(customer) {
      const idsucursal = this.form.idsucursal;
      const idusuarioRecepcion = this.form.idusuario_recepcion;
      this.form = {
        ...buildInitialForm(),
        idsucursal,
        idusuario_recepcion: idusuarioRecepcion,
        customer: {
          ...buildInitialCustomer(),
          idTypeOfDocument: this.documentTypes[0]?.id || null,
        },
      };
      this.customerSearch = customer?.nombres || "";
      this.customerMatches = [];
      this.customerSearchAttempted = false;
      this.articleSearch = "";
      this.articleMatches = [];
      this.articleSearchAttempted = false;
      this.historyResults = [];
      this.historyAttempted = false;
      this.historySearch = { search: "" };
      this.clearSelectedPhotos();
    },
    handlePhotoSelection(event) {
      const files = Array.from(event.target.files || []);

      if (!files.length) {
        return;
      }

      const nextPhotos = [...this.selectedPhotos];

      for (const file of files) {
        if (nextPhotos.length >= 6) {
          break;
        }

        nextPhotos.push({
          key: `${file.name}-${file.lastModified}-${nextPhotos.length}`,
          file,
          name: file.name,
          size: file.size,
          preview: URL.createObjectURL(file),
        });
      }

      this.selectedPhotos = nextPhotos;
      event.target.value = "";

      if (files.length + (this.selectedPhotos.length - files.length) > 6) {
        warningActions("Solo se permiten hasta 6 fotos por recepción.");
      }
    },
    removeSelectedPhoto(index) {
      const [removedPhoto] = this.selectedPhotos.splice(index, 1);

      if (removedPhoto?.preview) {
        URL.revokeObjectURL(removedPhoto.preview);
      }
    },
    clearSelectedPhotos() {
      this.selectedPhotos.forEach((photo) => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
      this.selectedPhotos = [];
    },
    async openCameraModalWithFacingMode(facingMode = "environment") {
      if (!navigator?.mediaDevices?.getUserMedia) {
        this.openPhotoPicker("camera");
        return;
      }

      try {
        await this.stopCameraStream();

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
          },
          audio: false,
        });

        this.cameraFacingMode = facingMode;
        this.cameraStream = stream;
        this.cameraModalOpen = true;

        this.$nextTick(() => {
          const cameraVideo = this.$refs.cameraVideo;
          if (!cameraVideo) {
            return;
          }

          cameraVideo.srcObject = stream;
          const playPromise = cameraVideo.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {
              this.openPhotoPicker("camera");
            });
          }
        });
      } catch (error) {
        this.cameraModalOpen = false;
        this.cameraStream = null;
        this.openPhotoPicker("camera");
      }
    },
    openPhotoPicker(type) {
      if (type === "camera") {
        this.openCameraModalWithFacingMode("environment");
        return;
      }

      const input = this.$refs.galleryPhotoInput;
      if (input && typeof input.click === "function") {
        input.click();
      }
    },
    async switchCameraFacingMode() {
      const nextFacingMode = this.cameraFacingMode === "environment" ? "user" : "environment";
      await this.openCameraModalWithFacingMode(nextFacingMode);
    },
    async capturePhotoFromCamera() {
      const cameraVideo = this.$refs.cameraVideo;
      if (!cameraVideo || this.cameraCaptureInProgress) {
        return;
      }

      this.cameraCaptureInProgress = true;

      try {
        const width = cameraVideo.videoWidth || 1280;
        const height = cameraVideo.videoHeight || 720;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) {
          warningActions("No se pudo inicializar la captura de cámara en este navegador.");
          return;
        }

        context.drawImage(cameraVideo, 0, 0, width, height);

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", 0.92);
        });

        if (!blob) {
          warningActions("No se pudo capturar la imagen de la cámara.");
          return;
        }

        const fileName = `camera-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });
        this.handlePhotoSelection({ target: { files: [file], value: "" } });
      } finally {
        this.cameraCaptureInProgress = false;
      }
    },
    async closeCameraModal() {
      this.cameraModalOpen = false;
      await this.stopCameraStream();
    },
    async stopCameraStream() {
      if (!this.cameraStream) {
        return;
      }

      this.cameraStream.getTracks().forEach((track) => track.stop());
      this.cameraStream = null;

      const cameraVideo = this.$refs.cameraVideo;
      if (cameraVideo) {
        cameraVideo.srcObject = null;
      }
    },
    async loadOrders() {
      this.loadingOrders = true;

      try {
        const params = new URLSearchParams({
          page: String(this.orderFilters.page),
          per_page: "8",
          idsucursal: String(this.form.idsucursal || 0),
        });

        if (this.orderFilters.search.trim()) {
          params.set("search", this.orderFilters.search.trim());
        }

        if (this.orderFilters.status) {
          params.set("status", this.orderFilters.status);
        }

        const response = await api.get(`taller/ordenes?${params.toString()}`);
        this.orders = response.data?.data || [];
        this.ordersPagination.currentPage = response.data?.current_page || 1;
        this.ordersPagination.totalPages = response.data?.last_page || 1;
        this.ordersLoaded = true;

        if (this.selectedOrder) {
          const selected = this.orders.find((order) => order.id === this.selectedOrder.id);
          if (!selected) {
            this.selectedOrder = null;
          } else {
            this.selectedOrder = {
              ...this.selectedOrder,
              ...selected,
            };
            this.syncStatusFormFromOrder();
          }
        }
      } catch (error) {
        errorActions(error);
      } finally {
        this.loadingOrders = false;
      }
    },
    async changeOrdersPage(direction) {
      const nextPage = this.orderFilters.page + direction;

      if (nextPage < 1 || nextPage > this.ordersPagination.totalPages) {
        return;
      }

      this.orderFilters.page = nextPage;
      await this.loadOrders();
    },
    selectOrder(order) {
      this.selectedOrder = order ? { ...order } : null;
      this.syncStatusFormFromOrder();
    },
    syncStatusFormFromOrder() {
      this.statusForm = {
        estado:
          this.selectedOrder?.estado && this.selectedOrder.estado !== "ENTREGADO"
            ? this.selectedOrder.estado
            : "LISTO_PARA_ENTREGA",
        diagnostico: this.selectedOrder?.diagnostico || "",
        solucion: this.selectedOrder?.solucion || "",
        observaciones: this.selectedOrder?.observaciones || "",
        presupuesto:
          this.selectedOrder?.presupuesto !== null && this.selectedOrder?.presupuesto !== undefined
            ? String(this.selectedOrder.presupuesto)
            : "",
        monto_pagado_total:
          this.selectedOrder?.monto_pagado_total !== null && this.selectedOrder?.monto_pagado_total !== undefined
            ? String(this.selectedOrder.monto_pagado_total)
            : "",
      };
    },
    async updateSelectedOrderStatus() {
      if (!this.selectedOrder?.id) {
        warningActions("Selecciona una orden antes de actualizar su estado.");
        return;
      }

      if (this.statusForm.estado === "ENTREGADO") {
        warningActions("El estado Entregado ya no se gestiona desde Taller. Debe completarse desde Caja.");
        return;
      }

      this.updatingStatus = true;

      try {
        const payload = {
          estado: this.statusForm.estado,
          diagnostico: this.normalizeOptional(this.statusForm.diagnostico),
          solucion: this.normalizeOptional(this.statusForm.solucion),
          observaciones: this.normalizeOptional(this.statusForm.observaciones),
          presupuesto: this.statusForm.presupuesto === "" ? null : Number(this.statusForm.presupuesto),
          monto_pagado_total: this.statusForm.monto_pagado_total === "" ? null : Number(this.statusForm.monto_pagado_total),
        };

        const response = await api.put(`taller/ordenes/${this.selectedOrder.id}/estado`, payload);
        this.selectedOrder = response.data?.order || null;
        this.syncOrderInListing(this.selectedOrder);
        this.syncStatusFormFromOrder();
        await successAction("Estado actualizado", "La orden de taller fue actualizada con éxito");
      } catch (error) {
        errorActions(error);
      } finally {
        this.updatingStatus = false;
      }
    },
    normalizeOptional(value) {
      const normalized = String(value || "").trim();
      return normalized === "" ? null : normalized;
    },
    appendOptionalToFormData(formData, key, value) {
      const normalized = this.normalizeOptional(value);

      if (normalized !== null) {
        formData.append(key, normalized);
      }
    },
    formatBytes(value) {
      const size = Number(value || 0);

      if (size < 1024) {
        return `${size} B`;
      }

      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
      }

      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatDate(value) {
      if (!value) {
        return "No disponible";
      }

      return new Intl.DateTimeFormat("es-DO", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
    },
    formatStatus(value) {
      return String(value || "")
        .replaceAll("_", " ")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
    },
    historyOrderCodeLabel(order) {
      const code = String(order?.codigo || "").trim();
      return code || "Sin código";
    },
    formatMoney(value) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) {
        return "0.00";
      }

      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    paymentSummary(order) {
      const totalPaid = Number(order?.monto_pagado_total || 0);
      const paidOnReception = Number(order?.monto_pagado_recepcion || 0);
      const details = Array.isArray(order?.details) ? order.details : [];
      const detailsTotal = details.reduce((accumulator, detail) => {
        const quantity = Number(detail?.cantidad || 0);
        const unitPrice = Number(detail?.precio_unitario || 0);
        return accumulator + (Number.isFinite(quantity) && Number.isFinite(unitPrice)
          ? Math.max(quantity, 0) * Math.max(unitPrice, 0)
          : 0);
      }, 0);
      const estimatedTotal = order?.presupuesto !== null && order?.presupuesto !== undefined
        ? Number(order.presupuesto || 0)
        : (detailsTotal > 0 ? detailsTotal : Number(order?.precio_chequeo || 0));
      const saldoPendiente = Math.max(estimatedTotal - totalPaid, 0);

      if (totalPaid <= 0) {
        return {
          estado: 'NO_PAGADO',
          label: 'No pagado',
          totalEstimado: estimatedTotal,
          saldoPendiente,
          totalPaid,
          paidOnReception,
        };
      }

      if (estimatedTotal > 0 && totalPaid >= estimatedTotal) {
        return {
          estado: 'PAGADO',
          label: 'Pagado',
          totalEstimado: estimatedTotal,
          saldoPendiente,
          totalPaid,
          paidOnReception,
        };
      }

      return {
        estado: 'AVANCE',
        label: estimatedTotal > 0 ? 'Con avance' : 'Pago registrado',
        totalEstimado: estimatedTotal,
        saldoPendiente,
        totalPaid,
        paidOnReception,
      };
    },
    paymentStatusClass(status) {
      if (status === 'PAGADO') {
        return 'bg-emerald-100 text-emerald-700';
      }
      if (status === 'AVANCE') {
        return 'bg-amber-100 text-amber-700';
      }
      return 'bg-slate-200 text-slate-600';
    },
    submissionCustomerMessage() {
      if (this.submissionResult?.customer_created) {
        return "Se creó un cliente nuevo automáticamente.";
      }

      return "Se reutilizó un cliente existente.";
    },
    submissionPublicTrackingUrl() {
      return this.resolvePublicTrackingLink(this.submissionResult) || "No disponible";
    },
    buildOrderDeviceLabel(order) {
      return [order?.tipo_equipo, order?.marca, order?.modelo]
        .filter(Boolean)
        .join(" - ") || "Equipo sin descripción";
    },
    resolvePublicTrackingLink(source) {
      const path = String(source?.public_tracking_path || '').trim();
      const url = String(source?.public_tracking_url || '').trim();

      if (path) {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        return `${window.location.origin}${normalizedPath}`;
      }

      if (!url) {
        return '';
      }

      try {
        const parsed = new URL(url, window.location.origin);
        const tracking = String(parsed.searchParams.get('tracking') || '').trim();

        if (tracking) {
          const business = String(parsed.searchParams.get('business') || 'negocio').trim() || 'negocio';
          return `${window.location.origin}/seguimiento/${encodeURIComponent(business)}/${encodeURIComponent(tracking)}`;
        }

        return parsed.toString();
      } catch (error) {
        return url;
      }
    },
    hasPublicTrackingLink(source) {
      return Boolean(this.resolvePublicTrackingLink(source));
    },
    statusClass(status) {
      if (status === "ENTREGADO") {
        return "bg-emerald-100 text-emerald-700";
      }
      if (status === "CANCELADO") {
        return "bg-rose-100 text-rose-700";
      }
      if (status === "LISTO_PARA_ENTREGA") {
        return "bg-blue-100 text-blue-700";
      }
      return "bg-amber-100 text-amber-700";
    },
    async copyPublicLink() {
      const publicLink = this.resolvePublicTrackingLink(this.submissionResult);

      if (!publicLink) {
        warningActions("Todavía no hay un enlace público disponible para copiar.");
        return;
      }

      try {
        await navigator.clipboard.writeText(publicLink);
        await successAction("Enlace copiado", "El enlace público fue copiado con éxito");
      } catch (error) {
        errorActions("No se pudo copiar el enlace público automáticamente.");
      }
    },
    async copySelectedOrderPublicLink() {
      const publicLink = this.resolvePublicTrackingLink(this.selectedOrder);

      if (!publicLink) {
        warningActions("Esta orden no tiene una ruta pública activa para copiar.");
        return;
      }

      try {
        await navigator.clipboard.writeText(publicLink);
        await successAction("Enlace copiado", "La ruta pública de esta orden fue copiada con éxito");
      } catch (error) {
        errorActions("No se pudo copiar la ruta pública de la orden.");
      }
    },
    async printSubmissionReceipt(format = 'letter') {
      if (!this.submissionResult?.order?.id) {
        warningActions("Todavía no hay una recepción registrada para imprimir.");
        return;
      }

      await printWorkshopReceipt({
        order: this.submissionResult.order,
        publicTrackingUrl: this.resolvePublicTrackingLink(this.submissionResult),
        publicTrackingPath: this.submissionResult.public_tracking_path,
        storeId: this.form.idsucursal,
        format,
      });
    },
    async printSelectedOrderReceipt(format = 'letter') {
      if (!this.selectedOrder?.id) {
        warningActions("Selecciona una orden antes de reimprimir el recibo.");
        return;
      }

      await printWorkshopReceipt({
        order: this.selectedOrder,
        publicTrackingUrl: this.resolvePublicTrackingLink(this.selectedOrder),
        publicTrackingPath: this.selectedOrder?.public_tracking_path,
        storeId: this.form.idsucursal,
        format,
      });
    },
  },
  beforeUnmount() {
    this.clearSelectedPhotos();
    this.stopCameraStream();
  },
};
</script>